import type { BuildingDef, BuildingLevelDef, RuleSet } from '../rules/schema';
import { fromMilli, toMilli } from './fixed';
import type { Building, ResourceId } from './types';

/** A level's numeric stats (not its cost, title or city-level requirement). */
type LevelStat = Exclude<
  keyof BuildingLevelDef,
  'cost' | 'title' | 'cityLevel' | 'shot' | 'upgradeTime'
>;

/** A building's level; missing means 1 (`level` is only written on upgrade). */
function buildingLevel(b: Building): number {
  return b.level ?? 1;
}

/** Highest level a building of this def can reach. */
function maxLevel(def: BuildingDef): number {
  return 1 + (def.levels?.length ?? 0);
}

/** The upgrade step that reaches `level` (2 and up), if the def has one. */
function levelDef(def: BuildingDef, level: number): BuildingLevelDef | undefined {
  return level >= 2 ? def.levels?.[level - 2] : undefined;
}

/** Display name of `level` (a city's era): the level's `title`, else level 1's, else the name. */
function levelTitle(def: BuildingDef, level: number): string {
  return (level >= 2 ? levelDef(def, level)?.title : undefined) ?? def.title ?? def.name;
}

/**
 * Lowest level the city holding a building of this def must have for an
 * upgrade to `level`: a placed building can't pass its city's level, and a
 * level may ask for more (`cityLevel`, an era unlock). 0 = no requirement.
 */
function upgradeCityLevel(def: BuildingDef, level: number): number {
  if (!def.placement) return 0;
  return Math.max(level, levelDef(def, level)?.cityLevel ?? 0);
}

function baseStat(def: BuildingDef, stat: LevelStat): number | undefined {
  switch (stat) {
    case 'hp':
      return def.hp;
    case 'territoryRadius':
      return def.territoryRadius;
    case 'visionRadius':
      return def.visionRadius;
    case 'rate':
      return def.provides?.rate;
    case 'workerSlots':
      return def.workerSlots;
    case 'capacity':
      return def.capacity;
    case 'trainSlots':
      return def.trainSlots;
    case 'cityHp':
      return def.cityHp;
    case 'cityStrength':
      return def.cityStrength;
    case 'strength':
      return def.strength;
    case 'range':
      return def.range;
    case 'watchRange':
      return def.watchRange;
    case 'watchRadius':
      return def.watchRadius;
    case 'cityVision':
      return def.cityVision;
    case 'gatherBonus':
    case 'repair':
      return undefined;
  }
}

/**
 * A stat at `level`: the value from the highest level up to `level` that
 * sets it, else the def's base value.
 */
function levelStat(def: BuildingDef, level: number, stat: LevelStat): number | undefined {
  for (let l = Math.min(level, maxLevel(def)); l >= 2; l--) {
    const value = levelDef(def, l)?.[stat];
    if (value !== undefined) return value;
  }
  return baseStat(def, stat);
}

/** Territory radius of a building at its current level; undefined if it gives no territory. */
function cityRadius(rules: RuleSet, b: Building): number | undefined {
  const def = rules.buildings[b.type];
  return def ? levelStat(def, buildingLevel(b), 'territoryRadius') : undefined;
}

/**
 * How far building `b`'s upgrade in progress has got (0..1); 0 with none.
 * Its progress is in thousandths of a second (`Building.upgrading`).
 */
function upgradeFraction(rules: RuleSet, b: Building): number {
  const def = rules.buildings[b.type];
  const time = b.upgrading && def && levelDef(def, b.upgrading.level)?.upgradeTime;
  if (!b.upgrading || !time) return 0;
  return Math.min(1, b.upgrading.progress / toMilli(time));
}

/**
 * Resources invested in building `b`: its cost plus every level it has been
 * raised to, plus an upgrade in progress at what was paid for it times
 * `upgradeShare` (1: in full, as the raze reward counts it; the timeout
 * score passes the progress, like a site). A building that consumes its builder (a city, founded by a
 * settler) also counts that unit's base cost, its founding value (sites too,
 * as the settler is spent when the site is placed). `levelWeight` scales
 * what the levels count (the timeout score weighs city levels lower).
 */
function buildingInvestment(
  rules: RuleSet,
  b: Building,
  levelWeight = 1,
  upgradeShare = 1
): Record<ResourceId, number> {
  const total: Record<ResourceId, number> = {};
  const def = rules.buildings[b.type];
  if (!def) return total;
  const add = (cost: Record<string, number> | undefined, weight = 1) => {
    for (const [res, v] of Object.entries(cost ?? {})) total[res] = (total[res] ?? 0) + v * weight;
  };
  add(def.cost);
  if (def.consumesBuilder) add(rules.units[def.builtBy]?.cost);
  for (let level = 2; level <= buildingLevel(b); level++)
    add(levelDef(def, level)?.cost, levelWeight);
  for (const [res, paid] of Object.entries(b.upgrading?.paid ?? {})) {
    total[res] = (total[res] ?? 0) + fromMilli(paid) * levelWeight * upgradeShare;
  }
  return total;
}

export type { LevelStat };
export {
  buildingInvestment,
  buildingLevel,
  cityRadius,
  levelDef,
  levelStat,
  levelTitle,
  maxLevel,
  upgradeCityLevel,
  upgradeFraction,
};
